import React, { useRef, useState, useEffect, memo, useCallback } from 'react';
import { Instagram, MessageCircle, Mail, MapPin, Send, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// WhatsApp configuration
const WHATSAPP_NUMBER = '917619546174';
const WHATSAPP_MESSAGE = 'Hello! I would like to inquire about your saree collection.';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

const ContactSection = memo(() => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    toast.success('Message sent successfully! We will get back to you soon.', {
      description: 'Thank you for contacting Shachi Collections.',
    });
    setFormData({ name: '', email: '', phone: '', message: '' });
  }, []);

  const contactInfo = [
    {
      icon: MessageCircle,
      label: 'WhatsApp',
      value: 'Place an order',
      href: WHATSAPP_URL,
      description: 'DM us',
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'shachicollections@gmail.com',
      href: 'mailto:shachicollections@gmail.com',
      description: 'Send us your queries',
    },
    {
      icon: Instagram,
      label: 'Instagram',
      value: '@shachi_collections',
      href: 'https://www.instagram.com/shachi_collections',
      description: 'Follow our latest collections',
    },
    {
      icon: MapPin,
      label: 'Visit Us',
      value: 'Bangalore, Karnataka',
      href: '#',
      description: 'By appointment only',
    },
  ];

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative py-24 sm:py-32 bg-card"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 75% 25%, hsl(43 74% 49%) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span
            className={`inline-flex items-center gap-2 font-elegant text-primary text-lg tracking-widest uppercase mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <MessageCircle className="w-5 h-5" />
            Get In Touch
          </span>
          <h2
            className={`text-4xl sm:text-5xl lg:text-6xl text-foreground mb-6 transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ fontFamily: 'Faith, Playfair Display, serif' }}
          >
            Contact <span className="text-gold-gradient">Us</span>
          </h2>
          <p
            className={`font-body text-foreground/60 text-lg max-w-2xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            We&apos;d love to hear from you. Reach out for inquiries about our collection or to schedule a personal consultation.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div
            className={`transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <div className="space-y-6 mb-10">
              {contactInfo.map((info, index) => (
                <a
                  key={info.label}
                  href={info.href}
                  target={info.href.startsWith('http') ? '_blank' : undefined}
                  rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="group flex items-start gap-4 p-4 rounded-sm glass border border-primary/10 hover:border-primary/30 transition-all duration-300"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-12 h-12 rounded-full glass-gold flex items-center justify-center group-hover:glow-gold transition-all duration-300 flex-shrink-0">
                    <info.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-body text-xs text-foreground/50 uppercase tracking-wider mb-1">
                      {info.label}
                    </p>
                    <p className="font-display text-lg text-foreground group-hover:text-primary transition-colors">
                      {info.value}
                    </p>
                    <p className="font-body text-sm text-foreground/50 mt-1">
                      {info.description}
                    </p>
                  </div>
                </a>
              ))}
            </div>

            {/* Business Hours */}
            <div className="p-6 rounded-sm glass border border-primary/10">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-5 h-5 text-primary" />
                <h4 className="font-display text-lg text-foreground">Business Hours</h4>
              </div>
              <div className="space-y-2 font-body text-sm">
                <div className="flex justify-between">
                  <span className="text-foreground/60">Monday - Saturday</span>
                  <span className="text-foreground">10:00 AM - 8:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground/60">Sunday</span>
                  <span className="text-primary">By Appointment</span>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div
            className={`transition-all duration-1000 delay-500 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <div className="p-6 sm:p-8 rounded-sm glass border border-primary/20">
              <h3 className="font-display text-2xl text-foreground mb-6">
                Quick Connect
              </h3>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block font-body text-sm text-foreground/70 mb-2">
                    Your Name
                  </label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your name"
                    required
                    className="bg-background/50 border-primary/20 text-foreground placeholder:text-foreground/40 focus:border-primary"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-body text-sm text-foreground/70 mb-2">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                      className="bg-background/50 border-primary/20 text-foreground placeholder:text-foreground/40 focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-sm text-foreground/70 mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+91 98765 43210"
                      className="bg-background/50 border-primary/20 text-foreground placeholder:text-foreground/40 focus:border-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-body text-sm text-foreground/70 mb-2">
                    Your Message
                  </label>
                  <Textarea
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us about your requirements..."
                    rows={5}
                    required
                    className="bg-background/50 border-primary/20 text-foreground placeholder:text-foreground/40 focus:border-primary resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-body py-6 text-lg group"
                >
                  Send Message
                  <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

ContactSection.displayName = 'ContactSection';

export default ContactSection;
